import SpintrAPI from "src/spintr/SpintrApi";
import type {
    AppDetail,
    AppListParams,
    AppListResponse,
    AiModelList,
    AppFileTree,
    AppSecretSummary,
    AppVersionListResponse,
    ConversationHistoryPage,
    ExecuteActionResponse,
    StudioAppBrief,
    StudioAppCounts,
    StudioAppUpdateRequest,
    StudioActivityResponse,
    StudioAppContract,
    StudioAppLifecycleState,
    StudioCurrentUser,
    StudioReviewInsights,
    StudioAppTypeValue,
    StudioForkResult,
    StudioSettings,
    StudioSettingsUpdateRequest,
    StudioStoreCategory,
    StudioStoreCategorySuggestion,
    StudioStoreListing,
    StudioStoreState,
    StoreAppDetail,
    StudioStreamingActive,
    StudioTileSummary,
    StudioTargetProjection,
    StudioWidgetData,
    StoreListResponse,
} from "src/studio/types";

export const studioApi = {
    getCurrentUser: () =>
        SpintrAPI.get<StudioCurrentUser>(`/api/v1/StudioAppBridge/me`).then(r => r.data),

    list: (params?: AppListParams) =>
        SpintrAPI.get<AppListResponse>(`/api/v1/studioapps`, { params }).then(r => r.data),

    getAppCounts: (params?: { hideDeleted?: boolean }) =>
        SpintrAPI.get<StudioAppCounts>(`/api/v1/studioapps/counts`, { params }).then(r => r.data),

    listStoreApps: (params?: { skip?: number; take?: number; searchText?: string; type?: StudioAppTypeValue }) =>
        SpintrAPI.get<StoreListResponse>(`/api/v1/studioapps/store`, { params }).then(r => r.data),

    // Fork a public store app into the current instance; returns the new, editable app.
    installStoreApp: (appId: number) =>
        SpintrAPI.post<StudioForkResult>(`/api/v1/studioapps/${appId}/install`, {}).then(r => r.data),

    // Duplicate one of the current instance's own apps into a new variant.
    duplicateApp: (appId: number) =>
        SpintrAPI.post<StudioForkResult>(`/api/v1/studioapps/${appId}/duplicate`, {}).then(r => r.data),

    get: (appId: number, versionId?: number) =>
        SpintrAPI.get<AppDetail>(
            versionId
                ? `/api/v1/studioapps/${appId}/versions/${versionId}`
                : `/api/v1/studioapps/${appId}`
        ).then(r => r.data),

    getPublished: (appId: number) =>
        SpintrAPI.get<AppDetail>(`/api/v1/studioapps/${appId}/published`).then(r => r.data),

    getReviewInsights: (appId: number, generate: boolean = true) =>
        SpintrAPI.get<StudioReviewInsights>(`/api/v1/studioapps/${appId}/review-insights`, { params: { generate } }).then(r => r.data),

    // Slice 7: the app's audit timeline (admin-only), newest first, paged via skip/take.
    getActivity: (appId: number, skip = 0, take = 25) =>
        SpintrAPI.get<StudioActivityResponse>(`/api/v1/studioapps/${appId}/activity`, { params: { skip, take } }).then(r => r.data),

    getWidgetData: (appId: number) =>
        SpintrAPI.get<StudioWidgetData>(`/api/v1/studioapps/${appId}/data`).then(r => r.data),

    // KPI tile data (Fas 2): the published version's getTileSummary result. 404 = the
    // app declares no tile summary (or the caller isn't targeted) — hide the tile.
    getTileSummary: (appId: number) =>
        SpintrAPI.get<StudioTileSummary>(`/api/v1/studioapps/${appId}/tile-summary`).then(r => r.data),

    // Usage telemetry: fire-and-forget, must never block or break navigation.
    trackTileOpened: (appId: number) => {
        SpintrAPI.post(`/api/v1/studioapps/${appId}/tile-opened`, {}).catch(() => { /* telemetry only */ });
    },

    listVersions: (appId: number, skip: number = 0, take: number = 10) =>
        SpintrAPI.get<AppVersionListResponse>(`/api/v1/studioapps/${appId}/versions`, {
            params: { skip, take },
        }).then(r => r.data),

    revertVersion: (appId: number, versionId: number) =>
        SpintrAPI.post<AppDetail>(
            `/api/v1/studioapps/${appId}/versions/${versionId}/revert`,
            {}
        ).then(r => r.data),

    listSecrets: (appId: number) =>
        SpintrAPI.get<AppSecretSummary[]>(`/api/v1/studioapps/${appId}/secrets`).then(r => r.data),

    upsertSecret: (appId: number, key: string, value: string) =>
        SpintrAPI.put<AppSecretSummary>(
            `/api/v1/studioapps/${appId}/secrets/${encodeURIComponent(key)}`,
            { Value: value }
        ).then(r => r.data),

    conversation: (appId: number, params?: { beforeOrderIndex?: number; take?: number }) =>
        SpintrAPI.get<ConversationHistoryPage>(`/api/v1/studioconversations/${appId}`, { params }).then(r => r.data),

    getActiveStream: (appId: number) =>
        SpintrAPI.get<StudioStreamingActive | null>(`/api/v1/studioconversations/${appId}/streaming`).then(r => r.data),

    // Persists the clean build; the RETURNED bundle is instrumented (inline-edit data-* attributes)
    // when available so the workbench preview can offer edit mode without a rebuild.
    bundle: (versionId: number, fileTree: AppFileTree) =>
        SpintrAPI.put<{ success: boolean; iife?: string; css?: string; error?: string; instrumented?: boolean }>(
            `/api/v1/studioapps/versions/${versionId}/bundle`,
            { fileTree }
        ).then(r => r.data),

    // Instrumented build (carries inline-edit data-* attributes) for the editable preview. Not persisted.
    previewBundle: (versionId: number) =>
        SpintrAPI.post<{ success: boolean; iife?: string; css?: string; error?: string; instrumented?: boolean }>(
            `/api/v1/studioapps/versions/${versionId}/preview-bundle`,
            {}
        ).then(r => r.data),

    // Replace one static text node located by tagger coordinates; returns the (possibly new draft)
    // version plus a fresh instrumented bundle so the editable preview keeps valid coordinates.
    // rich (optional) carries the whitelisted formatting node tree for formatted edits.
    inlineTextEdit: (
        versionId: number,
        edit: { filePath: string; line: number; column: number; newText: string; rich?: unknown[]; align?: string },
    ) =>
        SpintrAPI.post<{
            versionId: number;
            version: number;
            isPublished: boolean;
            iife?: string;
            css?: string;
            cleanIife?: string;
            cleanCss?: string;
            filePath?: string;
            fileContent?: string;
        }>(
            `/api/v1/studioapps/versions/${versionId}/inline-text`,
            edit
        ).then(r => r.data),

    // The assembled app metadata contract for a version (P0-6): what the version uses/may do.
    getContract: (versionId: number) =>
        SpintrAPI.get<StudioAppContract>(`/api/v1/studioapps/versions/${versionId}/contract`).then(r => r.data),

    // environment = the data namespace of the calling surface ("staging" in the workbench,
    // "prod" in the published viewer), matching what the bridge routes send. Omitting it makes
    // the server infer from the executed version, which splits reads from writes once the open
    // version is the published one — always pass it.
    executeAction: (versionId: number, actionName: string, parameters: Record<string, unknown>, environment?: string) =>
        SpintrAPI.post<ExecuteActionResponse>(
            `/api/v1/studioapps/versions/${versionId}/actions/${actionName}/execute`,
            { parameters, environment }
        ).then(r => r.data),

    saveFiles: (versionId: number, fileTree: AppFileTree) =>
        SpintrAPI.put<{ versionId: number; version: number; isPublished: boolean }>(
            `/api/v1/studioapps/versions/${versionId}/files`,
            { fileTree }
        ).then(r => r.data),

    publish: (appId: number) =>
        SpintrAPI.post<{ appId: number; publishedVersion: number; publishedVersionId: number }>(
            `/api/v1/studioapps/${appId}/publish`,
            {}
        ).then(r => r.data),

    // Lifecycle (P0-5): Customer Admin or Spintr admin. Archive implies unpublish;
    // restore does NOT republish. All return the app's lifecycle state.
    unpublish: (appId: number) =>
        SpintrAPI.post<StudioAppLifecycleState>(`/api/v1/studioapps/${appId}/unpublish`, {}).then(r => r.data),

    archive: (appId: number) =>
        SpintrAPI.post<StudioAppLifecycleState>(`/api/v1/studioapps/${appId}/archive`, {}).then(r => r.data),

    restoreArchived: (appId: number) =>
        SpintrAPI.post<StudioAppLifecycleState>(`/api/v1/studioapps/${appId}/restore`, {}).then(r => r.data),

    extendArchiveRetention: (appId: number) =>
        SpintrAPI.post<StudioAppLifecycleState>(`/api/v1/studioapps/${appId}/archive/extend`, {}).then(r => r.data),

    // App Market listing (Fas 2): Spintr admin only. Publish freezes the app's currently
    // published version as the store entry; call again to re-freeze or change designation
    // or the editorial content (category + catalog copy/screenshots). A null listing keeps
    // whatever content the row already has. Category: pass an existing categoryId OR a
    // typed newCategoryName (deduped server-side, then created with AI translations).
    storePublish: (appId: number, isOfficial: boolean, isForkable: boolean, categoryId?: number | null, newCategoryName?: string | null, listing?: StudioStoreListing | null) =>
        SpintrAPI.post<StudioStoreState>(
            `/api/v1/studioapps/${appId}/store/publish`,
            { isOfficial, isForkable, categoryId: categoryId ?? null, newCategoryName: newCategoryName ?? null, listing: listing ?? null }
        ).then(r => r.data),

    getStoreDetail: (appId: number) =>
        SpintrAPI.get<StoreAppDetail>(`/api/v1/studioapps/store/${appId}`).then(r => r.data),

    getStoreCategories: () =>
        SpintrAPI.get<StudioStoreCategory[]>(`/api/v1/studioapps/storecategories`).then(r => r.data),

    // AI category suggestion for the publish form; null when the AI has nothing useful (204).
    suggestStoreCategory: (appId: number) =>
        SpintrAPI.post<StudioStoreCategorySuggestion | undefined>(
            `/api/v1/studioapps/${appId}/store/suggest-category`, {}
        ).then(r => (r.status === 204 ? null : r.data ?? null)),

    storeUnlist: (appId: number) =>
        SpintrAPI.post<StudioStoreState>(`/api/v1/studioapps/${appId}/store/unlist`, {}).then(r => r.data),

    update: (appId: number, update: StudioAppUpdateRequest) =>
        SpintrAPI.put<{ appId: number; name: string }>(
            `/api/v1/studioapps/${appId}`,
            update
        ).then(r => r.data),

    toggleDelete: (appId: number) =>
        SpintrAPI.delete<{ appId: number; isDeleted: boolean }>(`/api/v1/studioapps/${appId}`).then(r => r.data),

    models: () =>
        SpintrAPI.get<AiModelList>(`/api/v1/studiosettings/models`).then(r => r.data),

    getSettings: () =>
        SpintrAPI.get<StudioSettings>(`/api/v1/studiosettings`).then(r => r.data),

    updateSettings: (update: StudioSettingsUpdateRequest) =>
        SpintrAPI.put<StudioSettings>(`/api/v1/studiosettings`, update).then(r => r.data),

    listCreators: () =>
        SpintrAPI.get<StudioTargetProjection[]>(`/api/v1/studiosettings/creators`).then(r => r.data),

    setCreator: (userId: number, enabled: boolean) =>
        SpintrAPI.put<void>(`/api/v1/studiosettings/creators/${userId}`, { enabled }).then(r => r.data),

    draftCustomerContext: () =>
        SpintrAPI.post<{ text: string }>(`/api/v1/studiosettings/customer-context/draft`, {}).then(r => r.data),

    getBrief: (appId: number) =>
        SpintrAPI.get<StudioAppBrief>(`/api/v1/studioapps/${appId}/brief`).then(r => r.data),

    updateBrief: (appId: number, brief: string) =>
        SpintrAPI.put<StudioAppBrief>(`/api/v1/studioapps/${appId}/brief`, { Brief: brief }).then(r => r.data),
};
